import React from "react";
import { EnumExpression, Expression } from "@hypertune/sdk/src/shared";
import toStartCase from "@hypertune/sdk/src/shared/helpers/toStartCase";
import {
  borderRadiusPx,
  optionsButtonRight,
  optionsButtonTop,
  singlePanelHeight,
} from "../../../../lib/constants";
import {
  ExpressionControlContext,
  IncludeExpressionOptionFunction,
} from "../../../../lib/types";
import Dropdown from "../../../../components/Dropdown";
import isReadOnly from "../../../../lib/expression/isReadOnly";
import { useHypertune } from "../../../../generated/hypertune.react";

export default function EnumExpressionControl({
  context,
  expression,
  setExpression,
  parentExpression,
  includeExpressionOption,
  shouldStack,
  optionsButton,
  dragHandle,
}: {
  context: ExpressionControlContext;
  expression: EnumExpression;
  setExpression: (newExpression: Expression | null) => void;
  parentExpression: Expression | null;
  includeExpressionOption: IncludeExpressionOptionFunction;
  shouldStack: boolean;
  optionsButton: React.ReactNode;
  dragHandle?: React.ReactNode;
}): React.ReactElement {
  const hypertuneRoot = useHypertune();
  const schemaEnumValues =
    context.commitContext.schema.enums[expression.valueType.enumTypeName]
      ?.values || {};

  function lowerCaseWordsExceptFirstCharacter(sentence: string): string {
    return sentence
      .split(" ")
      .map((word) => {
        if (word.length === 0) {
          return word;
        }

        const firstChar = word[0];
        const rest = word.slice(1).toLowerCase();

        return firstChar + rest;
      })
      .join(" ");
  }

  const options = Object.entries(schemaEnumValues)
    .filter(
      ([enumValue, valueSchema]) =>
        valueSchema.deprecationReason === undefined &&
        includeExpressionOption({
          expressionOption: {
            ...expression,
            value: enumValue,
          },
          expressionOptionParent: parentExpression,
        })
    )
    .map(([valueName]) => ({
      value: valueName,
      label: lowerCaseWordsExceptFirstCharacter(toStartCase(valueName)),
    }));

  const readOnly = isReadOnly(context);

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "row",
      }}
      onMouseDown={(event) => {
        if (hypertuneRoot.expressionsSelectability({ fallback: false })) {
          context.setExpressionEditorState({
            ...context.expressionEditorState,
            selectedItem: { type: "expression", id: expression.id },
          });
          event.stopPropagation();
        }
      }}
    >
      <Dropdown
        focusOnMount={
          context.expressionEditorState.selectedItem?.id === expression.id
        }
        intent={context.expressionIdToIntent?.[expression.id] ?? "neutral"}
        disabled={readOnly}
        hideIndicator={readOnly}
        style={{ flexGrow: 1 }}
        height={singlePanelHeight}
        valueContainerPaddingLeftOffset={dragHandle ? 19 : 0}
        indicatorsContainerWidthOffset={optionsButton ? 28 : 0}
        controlStyle={{
          borderRadius: shouldStack
            ? `0 0 ${borderRadiusPx}px ${borderRadiusPx}px`
            : borderRadiusPx,
        }}
        options={{ type: "options", options }}
        value={{
          value: expression.value,
          label: lowerCaseWordsExceptFirstCharacter(
            toStartCase(expression.value)
          ),
        }}
        onChange={(option) => {
          if (!option) {
            return;
          }
          setExpression({
            ...expression,
            value: option.value,
          });
        }}
        placeholder="Select enum value..."
        noOptionsMessage="No enum values found"
      />
      {dragHandle ? (
        <div
          style={{
            position: "absolute",
            top: 0,
            left: optionsButtonRight / 2,
          }}
        >
          <div className="-my-[12.5px]">{dragHandle}</div>
        </div>
      ) : null}
      {optionsButton ? (
        <div
          style={{
            position: "absolute",
            top: optionsButtonTop,
            right: optionsButtonRight,
          }}
        >
          {optionsButton}
        </div>
      ) : null}
    </div>
  );
}
